import React from "react";
import { Box, Checkbox, SegmentedControl, Text, Tooltip } from "@mantine/core";
import { IconLock } from "@tabler/icons-react";
import {
  ReportGranularity,
  ReportV2Definition,
  requiredGroupOf,
  ViewConfig,
} from "../../../../types/reportsV2";
import { ViewPatch } from "../hooks/useReportDraft";
import ArrowReorderList from "./ArrowReorderList";
import { toggleGroup } from "./customizeRules";
import SortableList from "./SortableList";
import Pill from "../../../common/Pill";
import styles from "./CustomizeDrawer.module.css";

export interface GroupByTabProps {
  definition: ReportV2Definition;
  view: ViewConfig;
  onUpdate: (patch: ViewPatch) => void;
  /** Phones: reorder with arrow buttons instead of dragging. */
  mobile?: boolean;
}

/**
 * Group by: the required Date group is locked first (with Day / Week /
 * Month), then the chosen fields in nesting order (drag, or the arrows on a
 * phone, to reorder). The sort is kept: it applies within every level,
 * whatever the grouping.
 */
export default function GroupByTab({
  definition,
  view,
  onUpdate,
  mobile = false,
}: GroupByTabProps) {
  const ReorderList = mobile ? ArrowReorderList : SortableList;
  const requiredLabel = requiredGroupOf(definition).label;
  const labelOf = (key: string) =>
    definition.group_bys.find((g) => g.key === key)?.label ?? key;
  const setGroups = (groups: string[]) => onUpdate({ groups });

  return (
    <>
      <Text className={styles.note}>
        {mobile
          ? "Rows nest in the order below. Use the arrows to reorder."
          : "Rows nest in the order below. Drag to reorder."}
      </Text>
      <Box className={styles.listHeading}>
        <Text component="span" className={styles.sectionLabel}>
          Selected
        </Text>
        <Text component="span" className={styles.sectionCount}>
          {view.groups.length + 1}
        </Text>
      </Box>
      <Box className={styles.selectedRow} data-locked>
        <Tooltip label={`${requiredLabel} is always the first group`}>
          <Box component="span" className={styles.lock}>
            <IconLock size={14} aria-hidden />
          </Box>
        </Tooltip>
        <Pill tone="tint">1</Pill>
        <Text component="span" className={styles.rowLabel}>
          {requiredLabel}
        </Text>
        <SegmentedControl
          size="xs"
          classNames={{ label: styles.granularityOption }}
          aria-label={`${requiredLabel} granularity`}
          value={view.granularity}
          onChange={(value) =>
            onUpdate({ granularity: value as ReportGranularity })
          }
          data={definition.granularities.map((g) => ({
            value: g.key,
            label: g.label,
          }))}
        />
      </Box>
      <ReorderList
        ariaLabel="Selected groupings"
        items={view.groups.map((key) => ({ id: key, label: labelOf(key) }))}
        firstNumber={2}
        canRemove
        onReorder={setGroups}
        onRemove={(key) => setGroups(view.groups.filter((k) => k !== key))}
      />
      {view.groups.length === 0 && (
        <Text className={styles.empty}>No groupings. Pick fields below.</Text>
      )}
      <Text className={styles.listHeading}>All fields</Text>
      <Box role="group" aria-label="All fields" className={styles.section}>
        {definition.group_bys.map((g) => (
          <Box key={g.key} className={styles.metricRow}>
            <Checkbox
              label={g.label}
              checked={view.groups.includes(g.key)}
              onChange={() => setGroups(toggleGroup(view.groups, g.key))}
            />
          </Box>
        ))}
      </Box>
    </>
  );
}
